import React, { useEffect, useState, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import * as walletService from '../../services/walletService'
import { getAffiliateMembers } from '../../services/affiliateService'
import type { AffiliateOutletContext } from './ModernAffiliateLayout'
import type { AffiliateMemberItem } from '../../services/affiliateService'
import { affiliateMutedText } from './affiliateUi'
import './affiliateLayout.css'

function formatJoinDate(joinedAt: string): string {
  try {
    const d = new Date(joinedAt)
    return Number.isNaN(d.getTime()) ? joinedAt : d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return joinedAt
  }
}

function maskMember(raw: string): string {
  const s = String(raw || '').trim()
  if (!s) return 'สมาชิก'
  if (s.length < 4) return s
  if (/^\d+$/.test(s)) {
    if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
    return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
  }
  if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
  return s.slice(0, 2) + '****' + s.slice(-3)
}

const AffiliateMemberPage: React.FC = () => {
  useOutletContext<AffiliateOutletContext>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [members, setMembers] = useState<AffiliateMemberItem[]>([])
  const [searchKeyword, setSearchKeyword] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const list = await getAffiliateMembers()
        if (!cancelled) setMembers(list)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const filteredMembers = useMemo(() => {
    if (!searchKeyword.trim()) return members
    const q = searchKeyword.trim().toLowerCase()
    return members.filter(
      (m) =>
        (m.username || '').toLowerCase().includes(q) ||
        (m.displayName || '').toLowerCase().includes(q) ||
        maskMember(m.username || '').toLowerCase().includes(q)
    )
  }, [members, searchKeyword])

  const totalBetAmount = 0

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2 c_tertiary">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="text-center py-4">
        <p className="c_primary mb-0">{error}</p>
      </div>
    )
  }

  return (
    <>
      <div className="row mx__1 mb-3">
        <div className="col-6 px-1">
          <div className="affiliate-stat-card h-100">
            <div className="affiliate-stat-card__label">จำนวนสมาชิก (คน)</div>
            <div className="affiliate-stat-card__value">{members.length}</div>
          </div>
        </div>
        <div className="col-6 px-1">
          <div className="affiliate-stat-card h-100">
            <div className="affiliate-stat-card__label">ยอดแทงรวม (บาท)</div>
            <div className="affiliate-stat-card__value affiliate-stat-card__value--accent">
              {walletService.formatCurrency(totalBetAmount)}
            </div>
          </div>
        </div>
      </div>

      <div className="row mx__1 mb-3">
        <div className="col-12 px-1">
          <div className="affiliate-panel p-3 p-md-4 overflow-hidden">
            <div className="d-flex align-items-center gap-2 mb-3">
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'rgba(86, 144, 255, 0.18)',
                }}
              >
                <i className="fas fa-users" style={{ color: '#93c5fd' }} />
              </div>
              <div>
                <h5 className="c_tertiary mb-0">สมาชิกในเครือข่าย</h5>
                <p className="mb-0 small" style={{ color: affiliateMutedText }}>
                  ค้นหาและดูรายละเอียดสมาชิกที่สมัครผ่านลิงก์ของคุณ
                </p>
              </div>
            </div>
            <div className="d-flex align-items-stretch" style={{ gap: 10, flexWrap: 'nowrap' }}>
              <input
                type="text"
                className="form-control affiliate-search-input"
                placeholder="ค้นหา username ที่ถูกปิดบัง..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
              />
              <button
                type="button"
                className="affiliate-search-btn border-0"
                onClick={() => setSearchKeyword(searchKeyword.trim())}
              >
                <i className="fas fa-search me-2" />
                ค้นหา
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
        {filteredMembers.length === 0 ? (
          <div className="col-12 px-1">
            <div className="affiliate-empty-state">
              <i className="fas fa-user-friends mb-3" style={{ fontSize: '2rem', color: '#64748b' }} />
              <h4>ไม่มีรายการสมาชิก</h4>
            </div>
          </div>
        ) : (
          filteredMembers.map((member, index) => (
            <div key={`${member.username}-${index}`} className="col-12 col-md-6 px-1">
              <div className="affiliate-member-card p-3 overflow-hidden h-100">
                <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                  <div className="text-start">
                    <h5 className="font_md mb-1" style={{ color: '#f8fafc' }}>
                      {maskMember(member.username || member.displayName || 'สมาชิก')}
                    </h5>
                    <small style={{ color: '#94a3b8' }}>แสดง username แบบปิดบังบางส่วน</small>
                  </div>
                  <span className="affiliate-member-card__rank">#{index + 1}</span>
                </div>
                <div className="small mb-3" style={{ color: affiliateMutedText }}>
                  สมัครเมื่อวันที่ {formatJoinDate(member.joinedAt)}{' '}
                  <span className="affiliate-status-pill ms-1">
                    {member.status || 'สมาชิกปกติ'}
                  </span>
                </div>
                <div className="row g-2 mx-0">
                  <div className="col-6 ps-0">
                    <div className="affiliate-member-metric affiliate-member-metric--primary">
                      <h6>เดิมพันทั้งหมด</h6>
                      <h5>{walletService.formatCurrency(0)}</h5>
                    </div>
                  </div>
                  <div className="col-6 pe-0">
                    <div className="affiliate-member-metric affiliate-member-metric--secondary">
                      <h6>คอมมิชชั่น</h6>
                      <h5>{walletService.formatCurrency(0)}</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  )
}

export default AffiliateMemberPage
